import React, { useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { ChevronDown } from "@piiqtechnologies/svg-icons";
import { PeekBox, PeekCheckbox, PeekCircularProgress, PeekDivider, PeekListItemButton, PeekPopover, PeekRadio, PeekStack, PeekTypography, useTheme, } from "@piiqtechnologies/ui-components";
import { SearchInput } from "@components/common/search-input";
import messages from "./messages.i18n";
export const MultiSelectDropdown = ({ options, selected, onChange, renderSummary, searchPlaceholder, startIcon, searchable = true, selectAll = true, singleSelect = false, disabled = false, loading = false, emptyMessage, selectAllLabel, onSearchChange, isAllSelected, onToggleAll, checkedColor, }) => {
    var _a;
    const intl = useIntl();
    const theme = useTheme();
    const triggerRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const serverSideSearch = Boolean(onSearchChange);
    const visibleOptions = useMemo(() => {
        if (serverSideSearch)
            return options;
        const term = search.trim().toLowerCase();
        if (!term)
            return options;
        return options.filter((option) => option.label.toLowerCase().includes(term));
    }, [options, search, serverSideSearch]);
    const visibleEnabledIds = useMemo(() => visibleOptions.filter((option) => !option.disabled).map((option) => option.id), [visibleOptions]);
    const allVisibleEnabledSelected = isAllSelected !== null && isAllSelected !== void 0 ? isAllSelected : (visibleEnabledIds.length > 0 && visibleEnabledIds.every((id) => selected.includes(id)));
    const handleSearch = (term) => {
        setSearch(term);
        onSearchChange === null || onSearchChange === void 0 ? void 0 : onSearchChange(term);
    };
    const closeMenu = () => {
        setOpen(false);
        handleSearch("");
    };
    const handleToggle = (option) => {
        if (option.disabled)
            return;
        if (singleSelect) {
            onChange([option.id]);
            closeMenu();
            return;
        }
        onChange(selected.includes(option.id) ? selected.filter((id) => id !== option.id) : [...selected, option.id]);
    };
    const handleToggleAll = () => {
        if (onToggleAll) {
            onToggleAll(!allVisibleEnabledSelected, visibleEnabledIds);
            return;
        }
        if (allVisibleEnabledSelected) {
            onChange(selected.filter((id) => !visibleEnabledIds.includes(id)));
        }
        else {
            onChange([...new Set([...selected, ...visibleEnabledIds])]);
        }
    };
    const handleTriggerKeyDown = (event) => {
        if (disabled)
            return;
        if (event.key === "Enter") {
            setOpen(true);
        }
        else if (event.key === " ") {
            event.preventDefault();
            setOpen(true);
        }
    };
    const rowSx = { gap: "10px", py: 0.75, px: 1 };
    const checkboxSx = {
        p: 0,
        color: theme.palette.grey[300],
        "&.Mui-checked": { color: checkedColor !== null && checkedColor !== void 0 ? checkedColor : theme.palette.teal[500] },
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(PeekBox, { ref: triggerRef, "data-testid": "multi-select-trigger", role: "button", "aria-expanded": open, "aria-disabled": disabled, tabIndex: disabled ? -1 : 0, onClick: () => !disabled && setOpen(true), onKeyDown: handleTriggerKeyDown, sx: {
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
                px: 1.5,
                py: 1.25,
                borderRadius: theme.spacing(1),
                border: "1px solid",
                borderColor: "grey.100",
                backgroundColor: "common.white",
            } },
            React.createElement(PeekStack, { direction: "row", gap: 1, alignItems: "center", justifyContent: "space-between" },
                React.createElement(PeekStack, { direction: "row", gap: 1, alignItems: "center" },
                    startIcon,
                    React.createElement(PeekTypography, { variant: "body1" }, renderSummary(selected.length))),
                React.createElement(PeekBox, { sx: { display: "flex", transition: "transform 0.2s ease", transform: open ? "rotate(180deg)" : "none" } },
                    React.createElement(ChevronDown, { width: 16, height: 16 })))),
        React.createElement(PeekPopover, { open: open, anchorEl: triggerRef.current, onClose: closeMenu, anchorOrigin: { vertical: "bottom", horizontal: "left" }, transformOrigin: { vertical: "top", horizontal: "left" }, sx: { ".MuiPaper-root": { width: (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth, maxHeight: 320, p: 1 } } },
            searchable && (React.createElement(PeekBox, { sx: { px: 0.5, pb: 1 } },
                React.createElement(SearchInput, { placeholder: searchPlaceholder, value: search, onChange: handleSearch, width: "100%" }))),
            selectAll && !singleSelect && visibleOptions.length > 0 && (React.createElement(React.Fragment, null,
                React.createElement(PeekListItemButton, { "data-testid": "multi-select-toggle-all", onClick: handleToggleAll, sx: rowSx },
                    React.createElement(PeekCheckbox, { size: "small", checked: allVisibleEnabledSelected, sx: checkboxSx, inputProps: { readOnly: true } }),
                    React.createElement(PeekTypography, { variant: "body1" }, selectAllLabel
                        ? selectAllLabel(visibleOptions.length)
                        : intl.formatMessage(allVisibleEnabledSelected ? messages.clearSelection : messages.selectAll))),
                React.createElement(PeekDivider, { light: true }))),
            loading && visibleOptions.length === 0 && (React.createElement(PeekStack, { alignItems: "center", py: 2 },
                React.createElement(PeekCircularProgress, { size: 20 }))),
            !loading && visibleOptions.length === 0 && (React.createElement(PeekTypography, { variant: "body1", color: "info.main", sx: { px: 0.5, py: 1 } }, emptyMessage !== null && emptyMessage !== void 0 ? emptyMessage : intl.formatMessage(messages.noResults))),
            visibleOptions.map((option, index) => (React.createElement(React.Fragment, { key: option.id },
                index > 0 && React.createElement(PeekDivider, { light: true }),
                React.createElement(PeekListItemButton, { "data-testid": `multi-select-option-${option.id}`, onClick: () => handleToggle(option), disabled: option.disabled, sx: Object.assign(Object.assign({}, rowSx), { opacity: option.disabled ? 0.5 : 1 }) },
                    singleSelect ? (React.createElement(PeekRadio, { size: "small", checked: selected.includes(option.id), disabled: option.disabled, sx: checkboxSx, inputProps: { readOnly: true } })) : (React.createElement(PeekCheckbox, { size: "small", checked: selected.includes(option.id), disabled: option.disabled, sx: checkboxSx, inputProps: { readOnly: true } })),
                    React.createElement(PeekTypography, { variant: "body1" }, option.label))))))));
};
export default MultiSelectDropdown;
